/**
 * Frak brand design system — single source of truth for every token used in
 * this Remotion project. Import from `src/brand` and never hard-code colors,
 * spacings, radii or shadows in scenes/components.
 */

export const brand = {
  colors: {
    neutral: {
      white: "#ffffff",
      black70: "#000000b2",
      grey50: "#f9fafb",
      grey100: "#f7f7f7",
      grey200: "#f5f5f5",
      grey250: "#e2e2e2",
      grey300: "#d4d4d4",
      grey400: "#a3a3a3",
      grey500: "#737373",
      grey600: "#525252",
      grey700: "#262626",
      grey800: "#000000",
    },
    primary: {
      50: "#f2f6fe",
      100: "#e5ecfd",
      200: "#ccd9fc",
      300: "#99b4f9",
      400: "#668ef5",
      500: "#3369f2",
      600: "#0043ef",
      700: "#0036bf",
      800: "#00288f",
    },
    success: {
      50: "#f3fcf7",
      100: "#e6f8f0",
      200: "#cef2e1",
      300: "#9de5c2",
      400: "#6bd8a4",
      500: "#3acb85",
      600: "#09be67",
      700: "#079852",
      800: "#05723e",
    },
    warning: {
      50: "#fef9f2",
      100: "#fdf2e5",
      200: "#fbe5cc",
      300: "#f7cb99",
      400: "#f4b266",
      500: "#f09833",
      600: "#ec7e00",
      700: "#bd6500",
      800: "#8e4c00",
    },
    error: {
      50: "#fef4f5",
      100: "#fce8ea",
      200: "#f9d2d6",
      300: "#f4a4ac",
      400: "#ee7783",
      500: "#e9495a",
      600: "#e31c31",
      700: "#b61627",
      800: "#88111d",
    },
  },
  scale: {
    0: "0",
    25: "1px",
    50: "2px",
    100: "4px",
    200: "8px",
    300: "12px",
    400: "16px",
    500: "20px",
    600: "24px",
    700: "28px",
    800: "32px",
    900: "36px",
    1000: "40px",
    1200: "48px",
    1300: "52px",
    1400: "56px",
    full: "9999px",
  },
  typography: {
    fontFamily: {
      inter: '"Inter", sans-serif',
      interTight: '"Inter Tight", sans-serif',
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800,
      black: 900,
    },
  },
} as const;

export const alias = {
  spacing: {
    none: "0",
    xxs: "4px",
    xs: "8px",
    s: "12px",
    m: "16px",
    l: "24px",
    xl: "32px",
  },
  size: {
    none: "0",
    xs: "4px",
    s: "8px",
    m: "16px",
    l: "36px",
    xl: "48px",
    xxl: "56px",
  },
  cornerRadius: {
    none: "0",
    xs: "4px",
    s: "8px",
    m: "12px",
    l: "16px",
    xl: "24px",
    xxl: "32px",
    full: "9999px",
  },
  borderWidth: {
    none: "0",
    xs: "1px",
    s: "1px",
  },
  neutral: {
    50: "#f9fafb",
    100: "#f7f7f7",
    200: "#f5f5f5",
    250: "#e2e2e2",
    300: "#d4d4d4",
    400: "#a3a3a3",
    500: "#737373",
    600: "#525252",
    700: "#262626",
    white: "#ffffff",
    overlay: "#000000b2",
    default: "#000000",
  },
  primary: {
    50: "#f2f6fe",
    100: "#e5ecfd",
    200: "#ccd9fc",
    300: "#99b4f9",
    400: "#668ef5",
    500: "#3369f2",
    700: "#0036bf",
    800: "#00288f",
    default: "#0043ef",
  },
  success: {
    50: "#f3fcf7",
    100: "#e6f8f0",
    200: "#cef2e1",
    300: "#9de5c2",
    400: "#6bd8a4",
    500: "#3acb85",
    700: "#079852",
    800: "#05723e",
    default: "#09be67",
  },
  warning: {
    50: "#fef9f2",
    100: "#fdf2e5",
    200: "#fbe5cc",
    300: "#f7cb99",
    400: "#f4b266",
    500: "#f09833",
    700: "#bd6500",
    800: "#8e4c00",
    default: "#ec7e00",
  },
  error: {
    50: "#fef4f5",
    100: "#fce8ea",
    200: "#f9d2d6",
    300: "#f4a4ac",
    400: "#ee7783",
    500: "#e9495a",
    700: "#b61627",
    800: "#88111d",
    default: "#e31c31",
  },
} as const;

export const semanticLight = {
  text: {
    primary: "#000000",
    secondary: "#525252",
    tertiary: "#a3a3a3",
    disabled: "#a3a3a3",
    action: "#0043ef",
    actionHover: "#0036bf",
    onAction: "#ffffff",
    error: "#e31c31",
    success: "#09be67",
    warning: "#ec7e00",
  },
  surface: {
    primary: "#0043ef",
    secondary: "#f2f6fe",
    background: "#ffffff",
    background2: "#f9fafb",
    elevated: "#ffffff",
    muted: "#f7f7f7",
    tertiary: "#f7f7f7",
    overlay: "#000000b2",
    disabled: "#e2e2e2",
    primaryHover: "#0036bf",
    primaryPressed: "#00288f",
    secondaryHover: "#e5ecfd",
    secondaryPressed: "#ccd9fc",
    error: "#fce8ea",
    success: "#f3fcf7",
    warning: "#fef9f2",
  },
  border: {
    subtle: "#f5f5f5",
    focus: "#a3a3a3",
    error: "#e31c31",
    success: "#09be67",
    warning: "#ec7e00",
    default: "#e2e2e2",
  },
  icon: {
    primary: "#000000",
    secondary: "#525252",
    tertiary: "#a3a3a3",
    disabled: "#a3a3a3",
    action: "#0043ef",
    actionHover: "#0036bf",
    onAction: "#ffffff",
    error: "#e31c31",
    success: "#09be67",
    warning: "#ec7e00",
  },
} as const;

export const semanticDark = {
  text: {
    primary: "#ffffff",
    secondary: "#f7f7f7",
    tertiary: "#f5f5f5",
    disabled: "#a3a3a3",
    action: "#668ef5",
    actionHover: "#0036bf",
    onAction: "#ffffff",
    error: "#e9495a",
    success: "#09be67",
    warning: "#ec7e00",
  },
  surface: {
    primary: "#0043ef",
    secondary: "#00288f",
    background: "#000000",
    background2: "#000000",
    elevated: "#262626",
    muted: "#525252",
    tertiary: "#737373",
    overlay: "#000000b2",
    disabled: "#e2e2e2",
    primaryHover: "#0036bf",
    primaryPressed: "#00288f",
    secondaryHover: "#e5ecfd",
    secondaryPressed: "#ccd9fc",
    error: "#b61627",
    success: "#05723e",
    warning: "#8e4c00",
  },
  border: {
    subtle: "#737373",
    focus: "#a3a3a3",
    error: "#e31c31",
    success: "#09be67",
    warning: "#ec7e00",
    default: "#525252",
  },
  icon: {
    primary: "#ffffff",
    secondary: "#f7f7f7",
    tertiary: "#f7f7f7",
    disabled: "#a3a3a3",
    action: "#0043ef",
    actionHover: "#0036bf",
    onAction: "#ffffff",
    error: "#e31c31",
    success: "#09be67",
    warning: "#ec7e00",
  },
} as const;

export const zIndex = {
  dropdown: 100,
  sticky: 200,
  fixed: 500,
  modal: 1000,
  popover: 1100,
  toast: 9999,
} as const;

export const transition = {
  fast: "0.15s",
  base: "0.2s",
  slow: "0.3s",
} as const;

export const easing = {
  default: "ease",
  inOut: "ease-in-out",
  smooth: "cubic-bezier(0.25, 0.1, 0.25, 1)",
  decelerate: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

export const shadow = {
  panel: "4px 4px 4px 0 rgba(0,0,0,0.08)",
  elevated: "0 2px 8px rgba(0,0,0,0.08)",
  dialog: "0 4px 24px rgba(0,0,0,0.12)",
} as const;

export const fontSize = {
  xxs: "10px",
  xs: "12px",
  s: "14px",
  m: "16px",
  l: "18px",
  xl: "20px",
  "2xl": "24px",
  "3xl": "28px",
  "4xl": "32px",
  "5xl": "40px",
  "6xl": "48px",
} as const;

export type Brand = typeof brand;
export type Alias = typeof alias;
export type SemanticLight = typeof semanticLight;
export type SemanticDark = typeof semanticDark;
export type Semantic = SemanticLight;
